export interface DramaStat {
  value: string;
  label: string;
  desc: string;
}

export interface Character {
  id: string;
  name: string;
  role: string;
  tier: 'lead' | 'support' | 'antagonist';
  position: string;
  summary: string;
  traits: string[];
  storyline: string;
  img: string;
  accent: 'primary' | 'accent' | 'secondary';
}

export interface Relationship {
  from: string;
  to: string;
  type: string;
  desc: string;
  accent: 'primary' | 'accent' | 'secondary';
}

export interface StoryNode {
  step: string;
  title: string;
  desc: string;
}

export interface Highlight {
  icon: string;
  title: string;
  desc: string;
}

export interface Faq {
  q: string;
  a: string;
}

export const navLinks = [
  { href: '#overview', label: '剧集速览' },
  { href: '#characters', label: '主要角色' },
  { href: '#cast', label: '演员表' },
  { href: '#relationships', label: '人物关系' },
  { href: '#storyline', label: '剧情脉络' },
  { href: '#faq', label: '常见问题' },
];

export const announcement = '全 32 集 · 8 个独立故事单元 · 都市悬疑犯罪普法栏目剧 · 爱奇艺热播';

export const dramaMeta = {
  name: '欲望的诱惑',
  tagline: '当欲望叩门，人性开始松动',
  genre: '都市悬疑 · 犯罪 · 普法栏目剧',
  episodes: '全 32 集',
  units: '8 个独立故事单元',
  platform: '爱奇艺',
  intro:
    '《欲望的诱惑》是一部以都市悬疑与犯罪为底色的普法栏目剧。它不靠单一主角从头讲到尾，而是用多个相对独立、又共享同一片都市江湖的故事单元，去追问同一个问题：当金钱、情感与恐惧同时逼近，一个人会做出怎样的选择。',
  intro2:
    '剧中人物很少是非黑即白的。有人被毒品控制、有人被困在义气与利益之间、有人把野心当成生存的盔甲。追剧的过程，也是看这些普通人在欲望面前一步步松动底线、再试图挽回的过程。',
};

export const stats: DramaStat[] = [
  { value: '32', label: '全集集数', desc: '全剧共 32 集，完整呈现多线故事' },
  { value: '8', label: '独立单元', desc: '由 8 个故事单元串联而成' },
  { value: '5', label: '核心角色', desc: '贯穿主线的关键人物' },
  { value: '3', label: '题材标签', desc: '悬疑 · 犯罪 · 普法警示' },
];

export const characters: Character[] = [
  {
    id: 'manting',
    name: '曼婷',
    role: '女主角',
    tier: 'lead',
    position: '全剧核心女主角，命运线贯穿多个单元',
    summary:
      '她原本只是想守住自己的生活，却被毒品与江湖恩怨一步步拖入漩涡。为保护在意的人，她在自明的威逼下被迫做出一个又一个身不由己的选择，是全剧情感张力最集中的角色。',
    traits: ['身不由己', '情感纠葛', '坚韧隐忍', '命运沉浮'],
    storyline: '曼婷 · 恩诚主线',
    img: 'https://readdy.ai/api/search-image?query=Single%20withered%20crimson%20rose%20on%20dark%20velvet%20with%20drifting%20smoke%2C%20muted%20amber%20light%2C%20melancholic%20cinematic%20still%20life%2C%20abstract%20metaphor%20of%20a%20woman%20trapped%20by%20desire%2C%20no%20people&width=800&height=1000&seq=desire-char-manting&orientation=portrait',
    accent: 'primary',
  },
  {
    id: 'encheng',
    name: '恩诚',
    role: '男主角',
    tier: 'lead',
    position: '身处武馆的男主角，悲剧色彩浓厚',
    summary:
      '他重情重义、行事隐忍，被广涛拉入制毒贩毒的圈子，也因与曼婷的交往而被自明怀疑。为了顾全大局，他数次选择牺牲自己，是全剧最令人心疼的男性角色。',
    traits: ['重情重义', '隐忍克制', '身处夹缝', '悲剧气质'],
    storyline: '曼婷 · 恩诚主线',
    img: 'https://readdy.ai/api/search-image?query=Rusty%20training%20gear%20inside%20an%20empty%20dim%20martial%20arts%20gym%2C%20warm%20amber%20rim%20light%2C%20drifting%20dust%20particles%2C%20quiet%20tension%20and%20restrained%20strength%2C%20cinematic%20still%20life%2C%20no%20people&width=800&height=1000&seq=desire-char-encheng&orientation=portrait',
    accent: 'secondary',
  },
  {
    id: 'guangtao',
    name: '广涛',
    role: '核心反派',
    tier: 'antagonist',
    position: '武馆实际掌权者，贩毒网络核心',
    summary:
      '他野心勃勃、手段狠辣，一方面把恩诚拉入自己的生意，一方面又不满自明分权。正因为他与自明之间不断升级的争斗，故事才被推向最极端的冲突。',
    traits: ['野心勃勃', '手段狠辣', '掌控欲强', '不择手段'],
    storyline: '武馆 · 制毒贩毒线',
    img: 'https://readdy.ai/api/search-image?query=Heavy%20brass%20key%20and%20scattered%20poker%20chips%20on%20dark%20polished%20wooden%20table%2C%20low%20warm%20light%2C%20deep%20shadows%2C%20moody%20crime%20drama%20still%20life%20about%20ambition%20and%20control%2C%20no%20people&width=800&height=1000&seq=desire-char-guangtao&orientation=portrait',
    accent: 'primary',
  },
  {
    id: 'ziming',
    name: '自明',
    role: '幕后操盘',
    tier: 'antagonist',
    position: '隐藏在幕后的操盘者，多疑善算',
    summary:
      '他很少站在台前，却始终在暗处拨动棋子。用毒品控制曼婷、指使她打探恩诚的底细，又在组织内部会议上试图削弱广涛的权力，是推动全局的那只手。',
    traits: ['幕后操盘', '多疑算计', '步步为营', '善于利用'],
    storyline: '幕后势力线',
    img: 'https://readdy.ai/api/search-image?query=Old%20rotary%20telephone%20and%20a%20smoldering%20cigarette%20in%20an%20ashtray%20on%20a%20dark%20office%20desk%2C%20faint%20amber%20glow%2C%20curling%20smoke%2C%20secretive%20and%20calculating%20mood%2C%20cinematic%20crime%20still%20life%2C%20no%20people&width=800&height=1000&seq=desire-char-ziming&orientation=portrait',
    accent: 'secondary',
  },
  {
    id: 'linzi',
    name: '林子',
    role: '关键配角',
    tier: 'support',
    position: '带江湖气息的关键人物，剧情推手',
    summary:
      '他为人义气却冲动，与恩诚往来密切。一次无意间的脱口而出，泄露了广涛制毒贩毒的秘密，也把恩诚彻底卷入了这场纷争，是故事由暗转明的关键节点。',
    traits: ['江湖义气', '冲动直率', '关键推手', '身不由己'],
    storyline: '江湖 · 关键线索线',
    img: 'https://readdy.ai/api/search-image?query=Broken%20neon%20sign%20reflection%20on%20wet%20asphalt%20at%20night%2C%20warm%20red%20and%20gold%20glow%2C%20drifting%20mist%2C%20restless%20streetwise%20urban%20atmosphere%2C%20cinematic%20still%20life%2C%20no%20people&width=800&height=1000&seq=desire-char-linzi&orientation=portrait',
    accent: 'accent',
  },
];

export const castTabs = [
  { key: 'all', label: '全部角色' },
  { key: 'lead', label: '核心主角' },
  { key: 'support', label: '关键人物' },
  { key: 'antagonist', label: '反面势力' },
] as const;

export const castNote =
  '说明：《欲望的诱惑》属于普法栏目剧形态，由多个独立单元组成，各单元多由栏目剧签约演员及特邀演员轮流出演，平台与官方并未发布像头部商业剧那样完整的明星主创名单。下表依据公开剧集信息中的角色与故事线整理，重点呈现人物定位与人物关系，供剧情查询参考。';

export const relationships: Relationship[] = [
  {
    from: '曼婷',
    to: '恩诚',
    type: '情感羁绊',
    desc: '两人在纷乱局势中相互牵挂，却又屡屡被推向对立，是全剧最揪心的情感线。',
    accent: 'primary',
  },
  {
    from: '自明',
    to: '曼婷',
    type: '控制与利用',
    desc: '自明用毒品控制曼婷，并指使她打探恩诚的底细，使她一步步沦为棋子。',
    accent: 'secondary',
  },
  {
    from: '广涛',
    to: '恩诚',
    type: '拉拢与提防',
    desc: '广涛把恩诚拉入自己的生意，两人既合作又彼此提防，矛盾不断累积。',
    accent: 'accent',
  },
  {
    from: '广涛',
    to: '自明',
    type: '权力对立',
    desc: '内部会议上自明试图分掉广涛的权力，广涛大为不服，双方彻底撕破脸。',
    accent: 'primary',
  },
  {
    from: '林子',
    to: '恩诚',
    type: '江湖同伴',
    desc: '林子与恩诚往来密切，却因一时失言，把秘密和恩诚一起拖入险境。',
    accent: 'accent',
  },
  {
    from: '广涛',
    to: '曼婷',
    type: '威逼决斗',
    desc: '广涛逼迫恩诚与曼婷决斗，成为全剧最虐心情节的导火索。',
    accent: 'secondary',
  },
];

export const storyline: StoryNode[] = [
  {
    step: '01',
    title: '毒品迷局',
    desc: '曼婷不幸被毒品控制，生活开始失控，逐渐成为幕后势力手中的工具。',
  },
  {
    step: '02',
    title: '大排档换钱',
    desc: '恩诚得知曼婷与林子有交往，并在大排档换钱，事情由此露出端倪。',
  },
  {
    step: '03',
    title: '自明起疑',
    desc: '幕后的自明对恩诚起了疑心，指使曼婷前去打探恩诚的底细。',
  },
  {
    step: '04',
    title: '武馆入伙',
    desc: '曼婷前往武馆劝恩诚离开，广涛却反其道而行，拉着恩诚一起干。',
  },
  {
    step: '05',
    title: '秘密泄露',
    desc: '林子无意间脱口说出广涛制毒贩毒的秘密，并把恩诚拉入自己一伙。',
  },
  {
    step: '06',
    title: '内部争权',
    desc: '自明组织内部会议想要分掉广涛的权力，广涛大为不服，冲突迅速升级。',
  },
  {
    step: '07',
    title: '决斗与代价',
    desc: '广涛逼迫恩诚与曼婷决斗，恩诚为顾全大局故意落败，曼婷被迫切下恩诚的手指。',
  },
];

export const highlights: Highlight[] = [
  {
    icon: 'ri-emotion-unhappy-line',
    title: '人性与欲望',
    desc: '没有脸谱化的善恶，每个人都在金钱、情感与恐惧中做选择，欲望如何吞噬理智是全剧最锋利的追问。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '普法警示',
    desc: '作为普法栏目剧，案件中往往伴随对法律后果的呈现，提醒观众在欲望面前守住底线。',
  },
  {
    icon: 'ri-radar-line',
    title: '悬疑节奏',
    desc: '多线并进、层层反转，毒品迷局与江湖恩怨交织，几乎每一集都在抛出新的疑问。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '情感张力',
    desc: '曼婷与恩诚的命运线充满克制与牺牲，虐心的抉择让人难以移开视线。',
  },
  {
    icon: 'ri-stack-line',
    title: '单元结构',
    desc: '全剧由 8 个相对独立的单元故事串联，既能独立观看，又能感受到相同的都市悬疑底色。',
  },
  {
    icon: 'ri-group-line',
    title: '群像刻画',
    desc: '从武馆同门到街头人物，配角各有立场，共同撑起一个有烟火气也危险的都市江湖。',
  },
];

export const faqs: Faq[] = [
  {
    q: '《欲望的诱惑》一共有多少集？',
    a: '公开剧集信息显示，全剧共 32 集，由 8 个相对独立的故事单元组成，部分角色会在多个单元中延续出现。',
  },
  {
    q: '《欲望的诱惑》主要演员有哪些？',
    a: '这是一部普法栏目剧，由各地栏目剧签约演员及特邀演员轮流出演各独立单元，官方并未发布完整的明星主创名单。本文按公开剧集信息，围绕曼婷、恩诚、广涛、自明、林子等核心角色整理角色介绍与人物关系。',
  },
  {
    q: '剧中的女主角是谁？',
    a: '从剧情比重看，曼婷是全剧的核心女主角。她的命运线与恩诚的故事贯穿多个单元，也是情感与冲突最集中的角色。',
  },
  {
    q: '这个故事讲的是什么？',
    a: '讲的是普通人被卷入毒品迷局与江湖恩怨后，在欲望、恐惧与情义之间反复挣扎的过程，兼具悬疑、犯罪与普法警示的元素。',
  },
  {
    q: '各单元之间有关联吗？',
    a: '各单元故事相对独立，但共享同一套都市悬疑、犯罪与普法的底色，部分角色和线索会跨单元出现，形成整体感。',
  },
  {
    q: '在哪里可以观看？',
    a: '该剧可在爱奇艺相关频道观看，具体上线集数与地区以平台实际展示为准。',
  },
];

export const watchGuide = [
  { icon: 'ri-play-circle-line', label: '播出平台', value: '爱奇艺' },
  { icon: 'ri-list-check-3', label: '剧集长度', value: '全 32 集' },
  { icon: 'ri-layout-grid-line', label: '单元结构', value: '8 个独立故事' },
  { icon: 'ri-price-tag-3-line', label: '题材类型', value: '悬疑 / 犯罪 / 普法' },
];